import React, { useEffect, useState } from 'react';
import { Download, Edit3, Loader2, ArrowLeft, Languages, RefreshCw, Trash2, MoreVertical, Menu, X, FileText } from 'lucide-react';
import { analyzeResumeGaps } from '../../utils/progressSync';
import { 
  ToolbarButton, 
  ToolbarIconButton, 
  ToolbarButtonWithTooltip, 
  ToolbarSeparator,
  ProgressGuideModal
} from '../common';
import CoverLetterModal from '../coverLetter/CoverLetterModal';

// 모바일 체크 훅
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const ResumeToolbar = ({
  resumeData,
  sessionData,
  isEditing,
  isDownloading,
  actualProgress,
  existingPortfolio,
  portfolioLoading,
  onEdit,
  onDownload,
  onContinueConsulting,
  onPortfolioModal,
  onDelete,
  isOnboardingActive,
  onboardingStep,
  onGenerateEnglishVersion,
  isGeneratingEnglish,
  englishGenerationError,
  currentLanguage,
  onLanguageChange,
  englishUpdateNeeded,
  englishUpdateInfo,
  showInfo,
  selectedTemplateId,
  sessionId,
  userId
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [progressModalConfig, setProgressModalConfig] = useState({
    isOpen: false,
    featureName: ''
  });
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  
  // 10초마다 편집 가이드 하이라이트 애니메이션
  useEffect(() => {
    if (isEditing) return; // 편집 모드일 때는 애니메이션 비활성화
    
    const intervalId = setInterval(() => {
      const editGuideElement = document.querySelector('.toolbar-edit-guide-text');
      const editButtonElement = document.querySelector('.edit-button');
      
      if (editGuideElement) {
        editGuideElement.classList.add('toolbar-edit-guide-highlight');
        setTimeout(() => {
          editGuideElement.classList.remove('toolbar-edit-guide-highlight');
        }, 3000);
      }
      
      if (editButtonElement) {
        editButtonElement.classList.add('toolbar-edit-button-highlight');
        setTimeout(() => {
          editButtonElement.classList.remove('toolbar-edit-button-highlight');
        }, 3000);
      }
    }, 10000); // 10초마다
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isEditing]);
  return (
    <div className="toolbar-wrapper">
      <div className="toolbar-container" data-tour="auto-save">
        <div className="toolbar-left">
          <h3 className="toolbar-title">
            <span className="toolbar-title-text">
              {(() => {
                const name = resumeData.name || '이름';
                // 이름에서 _YYYY-MM-DD 패턴 제거
                return name.replace(/_\d{4}-\d{2}-\d{2}$/, '');
              })()}님의 이력서
            </span>
          </h3>
          
          {!isEditing && (
            <>
              <EditButton
                isEditing={isEditing}
                isOnboardingActive={isOnboardingActive}
                onboardingStep={onboardingStep}
                onEdit={onEdit}
              />
              
              {/* 편집 가이드 - 태블릿 이상에서만 표시 */}
              <div className="toolbar-edit-guide hidden md:flex">
                <ArrowLeft className="toolbar-edit-guide-icon" />
                <span className="toolbar-edit-guide-text">편집 기능을 이용하여 직접 세부 내용을 수정하고 보완해주세요</span>
              </div>
            </>
          )}
        </div>
        
        {/* 데스크톱 툴바 */}
        <div className="toolbar-right hidden sm:flex">
        
        <LanguageToggleButton
          resumeData={resumeData}
          sessionData={sessionData}
          actualProgress={actualProgress}
          isGeneratingEnglish={isGeneratingEnglish}
          englishGenerationError={englishGenerationError} // 에러 상태 전달
          onGenerateEnglishVersion={onGenerateEnglishVersion}
          currentLanguage={currentLanguage}
          onLanguageChange={onLanguageChange}
          englishUpdateNeeded={englishUpdateNeeded}
          englishUpdateInfo={englishUpdateInfo}
        />
        
        <DownloadButton
          resumeData={resumeData}
          actualProgress={actualProgress}
          isDownloading={isDownloading}
          onDownload={onDownload}
        />
        
        <CoverLetterButton
          actualProgress={actualProgress}
          onClick={() => setShowCoverLetterModal(true)}
        />
        
        <PortfolioButton
          resumeData={resumeData}
          actualProgress={actualProgress}
          portfolioLoading={portfolioLoading}
          existingPortfolio={existingPortfolio}
          onPortfolioModal={onPortfolioModal}
        />
          
          {/* 삭제 버튼을 맨 오른쪽에 배치하고 구분선으로 분리 */}
          <div className="ml-auto flex items-center gap-2">
            <ToolbarSeparator />
            <ToolbarIconButton
              icon={Trash2}
              onClick={onDelete}
              variant="danger"
              title="이력서 삭제"
            />
          </div>
        </div>
        
        {/* 모바일 메뉴 버튼 */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMobileMenuOpen(prev => !prev);
          }}
          className="sm:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors relative z-50"
          aria-label="메뉴 열기"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      
      {/* 모바일 드롭다운 메뉴 */}
      {isMobileMenuOpen && (
        <div className="sm:hidden border-t border-gray-200 bg-white relative z-50">
          <div className="px-4 py-3 space-y-2">
            {/* 편집 가이드 - 모바일에서 */}
            {!isEditing && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-gray-50 rounded-lg mb-2">
                <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                <span>편집하여 내용을 수정해주세요</span>
              </div>
            )}
            
            {/* 언어 선택 */}
            <div className="pb-2 border-b border-gray-100">
              <LanguageToggleButton
                resumeData={resumeData}
                sessionData={sessionData}
                actualProgress={actualProgress}
                isGeneratingEnglish={isGeneratingEnglish}
                englishGenerationError={englishGenerationError}
                onGenerateEnglishVersion={onGenerateEnglishVersion}
                currentLanguage={currentLanguage}
                onLanguageChange={onLanguageChange}
                englishUpdateNeeded={englishUpdateNeeded}
                englishUpdateInfo={englishUpdateInfo}
              />
            </div>
            
            {/* 액션 버튼들 */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (Number(actualProgress) === 100) {
                  onDownload();
                  setIsMobileMenuOpen(false);
                } else {
                  setProgressModalConfig({
                    isOpen: true,
                    featureName: 'PDF 다운로드'
                  });
                }
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                actualProgress === 100
                  ? 'text-gray-700 hover:bg-gray-50'
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Download className="w-4 h-4" />
              PDF 다운로드 {actualProgress !== 100 && `(${actualProgress}% 완성)`}
            </button>
            
            {existingPortfolio ? (
              <button
                onClick={() => {
                  window.open(`/portfolio/${existingPortfolio.slug}`, '_blank');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="text-base">🚀</span>
                내 웹페이지 가기
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (Number(actualProgress) === 100) {
                    onPortfolioModal();
                    setIsMobileMenuOpen(false);
                  } else {
                    setProgressModalConfig({
                      isOpen: true,
                      featureName: '웹페이지 생성'
                    });
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                  actualProgress === 100
                    ? 'text-gray-700 hover:bg-gray-50'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                <span className="text-base">💎</span>
                내 웹페이지 만들기 {actualProgress !== 100 && `(${actualProgress}% 완성)`}
              </button>
            )}
            
            {/* 삭제 버튼 */}
            <div className="border-t border-gray-100 pt-2">
              <button
                onClick={() => {
                  onDelete();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                이력서 삭제
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 진행률 안내 모달 */}
      <ProgressGuideModal
        isOpen={progressModalConfig.isOpen}
        onClose={() => setProgressModalConfig({ isOpen: false, featureName: '' })}
        actualProgress={actualProgress}
        resumeData={resumeData}
        featureName={progressModalConfig.featureName}
        onContinueConsulting={onContinueConsulting}
        onEdit={!isEditing ? onEdit : undefined}
      />
      
      {/* 자소서 작성 모달 */}
      {showCoverLetterModal && (
        <CoverLetterModal
          isOpen={showCoverLetterModal}
          onClose={() => setShowCoverLetterModal(false)}
          sessionId={sessionId}
          userId={userId}
          resumeData={resumeData}
        />
      )}
    </div>
  );
};

// 편집 버튼 컴포넌트
const EditButton = ({ isEditing, isOnboardingActive, onboardingStep, onEdit }) => {
  const isOnboardingEdit = isOnboardingActive && onboardingStep === 'resume-edit' && isEditing;
  
  return (
    <ToolbarButton
      onClick={() => {
        if (isOnboardingEdit) return;
        if (!isEditing) {
          onEdit(); // 편집 모드 시작만 처리
        }
      }}
      disabled={isOnboardingEdit}
      variant="default"
      title={
        isOnboardingEdit 
          ? '온보딩 중에는 편집 모드를 종료할 수 없습니다' 
          : isEditing
          ? '편집 중 - 좌측 편집 완료 버튼을 사용하세요'
          : '편집하기'
      }
      isActive={isEditing}
      className="edit-button"
    >
      {isEditing ? (
        <span className="toolbar-button-icon">✍️</span>
      ) : (
        <Edit3 className="w-4 h-4" />
      )}
      <span className="hidden sm:inline">
        {isOnboardingEdit 
          ? '온보딩 진행 중' 
          : isEditing
          ? '편집 중'
          : '편집하기'
        }
      </span>
    </ToolbarButton>
  );
};

// 다운로드 버튼 컴포넌트 - 복잡한 툴팁 사용
const DownloadButton = ({ resumeData, actualProgress, isDownloading, onDownload }) => {
  const isComplete = actualProgress === 100;
  const { message: gapMessage } = analyzeResumeGaps(resumeData);
  
  // 툴팁 내용 생성
  const getTooltipContent = () => {
    if (isDownloading) {
      return "잠시만 기다려주세요. PDF를 생성하고 있습니다...";
    }
    
    if (isComplete) {
      return 'PDF로 저장하기 - 인쇄 대화상자에서 "대상"을 "PDF로 저장"으로 선택하세요';
    }
    
    // 90%인 경우 편집하기 가이드 메시지 추가
    const getGuideMessage = () => {
      if (actualProgress === 90) {
        return (
          <div className="text-yellow-200 mt-2 pt-2 border-t border-gray-600">
            <div className="font-semibold mb-1">💡 완성 방법</div>
            <div>좌측의 "편집하기" 버튼을 클릭하여 연락처 정보를 입력해주세요</div>
          </div>
        );
      }
      return null;
    };

    return (
      <div>
        <div className="font-semibold mb-1">📄 PDF 다운로드 조건</div>
        <div className="text-gray-300 mb-2">이력서를 100% 완성해야 PDF 다운로드가 가능합니다</div>
        <div className="text-gray-300">
          <div className="font-semibold">현재 진행률: {actualProgress}%</div>
          <div>{gapMessage}</div>
        </div>
        {getGuideMessage()}
      </div>
    );
  };

  return (
    <ToolbarButtonWithTooltip
      onClick={isComplete ? onDownload : undefined}
      disabled={isDownloading || !isComplete}
      variant="default"
      title={isComplete && !isDownloading ? 'PDF로 저장하기' : ''}
      dataTour="pdf-download"
      tooltip={getTooltipContent()}
      showTooltip={!isComplete || isDownloading}
      tooltipPosition="bottom"
    >
      {isDownloading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <span className="toolbar-button-icon">📥</span>
      )}
      <span className="hidden xl:inline">{isDownloading ? '다운로드 중...' : 'PDF 다운로드'}</span>
    </ToolbarButtonWithTooltip>
  );
};

// 자소서 작성 버튼 컴포넌트
const CoverLetterButton = ({ actualProgress, onClick }) => {
  const isComplete = actualProgress === 100;
  
  const getTooltipContent = () => {
    if (isComplete) {
      return '세션 대화 내용을 기반으로 자기소개서를 작성합니다';
    }
    
    return (
      <div>
        <div className="font-semibold mb-1">📝 자소서 작성 조건</div>
        <div className="text-gray-300 mb-2">이력서를 100% 완성해야 자소서 작성이 가능합니다</div>
        <div className="text-gray-300">
          <div className="font-semibold">현재 진행률: {actualProgress}%</div>
        </div>
      </div>
    );
  };
  
  return (
    <ToolbarButtonWithTooltip
      onClick={isComplete ? onClick : undefined}
      disabled={!isComplete}
      variant="default"
      title={isComplete ? '자소서 작성하기' : ''}
      tooltip={getTooltipContent()}
      showTooltip={!isComplete}
      tooltipPosition="bottom"
    >
      <span className="toolbar-button-icon">📝</span>
      <span className="hidden xl:inline">자소서 작성하기</span>
    </ToolbarButtonWithTooltip>
  );
};

// 포트폴리오 버튼 컴포넌트
const PortfolioButton = ({ resumeData, actualProgress, portfolioLoading, existingPortfolio, onPortfolioModal }) => {
  const isComplete = actualProgress === 100;
  const { message: gapMessage } = analyzeResumeGaps(resumeData);

  if (portfolioLoading) {
    return (
      <ToolbarButton disabled variant="default">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="hidden sm:inline">확인 중...</span>
      </ToolbarButton>
    );
  }

  // 기존 포트폴리오가 있는 경우
  if (existingPortfolio) {
    return (
      <ToolbarButton
        onClick={() => {
          window.open(`/portfolio/${existingPortfolio.slug}`, '_blank');
        }}
        variant="default"
        title="내 웹페이지 보기"
      >
        <span className="toolbar-button-icon">🚀</span>
        <span className="hidden xl:inline">내 웹페이지 가기</span>
      </ToolbarButton>
    );
  }

  // 새 포트폴리오 생성 버튼
  const getTooltipContent = () => {
    // 90%인 경우 편집하기 가이드 메시지 추가
    const getGuideMessage = () => {
      if (actualProgress === 90) {
        return (
          <div className="text-yellow-200 mt-2 pt-2 border-t border-gray-600">
            <div className="font-semibold mb-1">💡 완성 방법</div>
            <div>좌측의 "편집하기" 버튼을 클릭하여 연락처 정보를 입력해주세요</div>
          </div>
        );
      }
      return null;
    };

    return (
      <div>
        <div className="font-semibold mb-1">💎 웹페이지 생성 조건</div>
        <div className="text-gray-300 mb-2">이력서를 100% 완성해야 웹페이지를 생성할 수 있습니다</div>
        <div className="text-gray-300">
          <div className="font-semibold">현재 진행률: {actualProgress}%</div>
          <div>{gapMessage}</div>
        </div>
        {getGuideMessage()}
      </div>
    );
  };

  return (
    <ToolbarButtonWithTooltip
      onClick={isComplete ? onPortfolioModal : undefined}
      disabled={!isComplete}
      variant="default"
      title={isComplete ? '내 웹페이지 만들기' : ''}
      dataTour="portfolio-create"
      tooltip={getTooltipContent()}
      showTooltip={!isComplete}
      tooltipPosition="bottom"
    >
      <span className="toolbar-button-icon">💎</span>
      <span className="hidden xl:inline">내 웹페이지 만들기</span>
    </ToolbarButtonWithTooltip>
  );
};

// 언어 선택 라디오 버튼 컴포넌트 (영어 버전이 있으면 한국어/English 선택, 없으면 생성 버튼)
const LanguageToggleButton = ({ 
  resumeData, 
  sessionData, 
  actualProgress, 
  isGeneratingEnglish, 
  englishGenerationError, // 에러 상태 추가
  onGenerateEnglishVersion,
  currentLanguage,
  onLanguageChange,
  englishUpdateNeeded,
  englishUpdateInfo
}) => {
  const isComplete = actualProgress === 100;
  const hasEnglishVersion = resumeData?.english_version;
  
  // 영어 버전이 있는 경우 - 언어 선택 라디오 버튼 표시
  if (hasEnglishVersion) {
    return (
      <div className="toolbar-language-selector">
        {/* 한국어 라디오 버튼 */}
        <button
          onClick={() => onLanguageChange('kr')}
          className={`toolbar-language-button ${currentLanguage === 'kr' ? 'active' : ''}`}
          title="한국어 버전 보기"
        >
          <div className={`toolbar-language-radio ${currentLanguage === 'kr' ? 'active' : ''}`}>
            {currentLanguage === 'kr' && (
              <div className="toolbar-language-radio-dot"></div>
            )}
          </div>
          <span>한국어</span>
        </button>
        
        {/* 영어 라디오 버튼 */}
        <button
          onClick={async () => {
            // 먼저 언어를 English로 변경 (즉시 라디오 버튼 선택 효과)
            onLanguageChange('en');
            
            // 업데이트가 필요하거나 에러 상태인 경우에만 동기화 실행
            if ((englishUpdateNeeded || englishGenerationError) && !isGeneratingEnglish && isComplete) {
              onGenerateEnglishVersion();
            }
          }}
          disabled={isGeneratingEnglish}
          className={`toolbar-language-button ${currentLanguage === 'en' ? 'active' : ''} ${isGeneratingEnglish ? 'disabled' : ''}`}
          title={
            isGeneratingEnglish
            ? '영어 버전 동기화 중...'
            : 'English Version 보기'
          }
        >
          <div className={`toolbar-language-radio ${currentLanguage === 'en' ? 'active' : ''}`}>
            {currentLanguage === 'en' && (
              <div className="toolbar-language-radio-dot"></div>
            )}
          </div>
          
          {isGeneratingEnglish ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>동기화 중...</span>
            </>
          ) : (
            <>
              {englishGenerationError ? (
                <RefreshCw className="w-3 h-3" />
              ) : null}
              <span>English</span>
              {englishUpdateNeeded && (
                <span className="toolbar-language-status">• 동기화 필요</span>
              )}
              {englishGenerationError && (
                <span className="toolbar-language-status toolbar-language-status-error">• 재시도 필요</span>
              )}
            </>
          )}
        </button>
      </div>
    );
  }
  
  // 영어 버전이 없는 경우 - 생성 버튼 표시
  const getTooltipContent = () => {
    if (isGeneratingEnglish) {
      return "영어 버전을 생성하고 있습니다. 잠시만 기다려주세요...";
    }
    
    if (!isComplete) {
      return (
        <div>
          <div className="font-semibold mb-1">🌍 영어 버전 생성 조건</div>
          <div className="text-gray-300 mb-2">이력서를 100% 완성해야 영어 버전을 생성할 수 있습니다</div>
          <div className="text-gray-300">
            <div className="font-semibold">현재 진행률: {actualProgress}%</div>
          </div>
        </div>
      );
    }
    
    return "한국어 이력서를 영어로 번역하여 영어 버전을 생성합니다";
  };

  const isMobile = useIsMobile();
  
  // 모바일에서의 클릭 핸들러
  const handleClick = () => {
    if (isComplete) {
      onGenerateEnglishVersion();
    } else if (isMobile) {
      // 모바일에서는 모달 표시
      setProgressModalConfig({
        isOpen: true,
        featureName: '영어 버전 생성'
      });
    }
  };
  
  return (
    <ToolbarButtonWithTooltip
      onClick={handleClick}
      disabled={isGeneratingEnglish || (!isComplete && !isMobile)}
      variant="default"
      title={isComplete && !isGeneratingEnglish ? '영어 이력서 만들기' : ''}
      tooltip={getTooltipContent()}
      showTooltip={!isComplete || isGeneratingEnglish}
      tooltipPosition="bottom"
    >
      {isGeneratingEnglish ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : englishGenerationError ? (
        <RefreshCw className="w-4 h-4" />
      ) : (
        <span className="toolbar-button-icon">🌍</span>
      )}
      <span className="hidden xl:inline">{isGeneratingEnglish ? '생성 중...' : englishGenerationError ? '재시도하기' : '영어 이력서 만들기'}</span>
    </ToolbarButtonWithTooltip>
  );
};

export default ResumeToolbar;
